<script lang="ts" setup generic="Value extends string | number">
import type { IconVariantType, VariantType } from '#components/elements/input';
import InputBase from '#components/elements/input/InputBase.vue';
import type { IconName } from '#components/icons/icon.model';
import { Listbox, ListboxButton, ListboxOptions } from '@headlessui/vue';
import type { Ref } from 'vue';
import { computed, onBeforeMount, ref, useSlots } from 'vue';
import type { InputSelectOptionType } from './InputSelectOption.vue';
import InputSelectOption from './InputSelectOption.vue';

const {
  label,
  variant = 'default',
  placeholder = '',
  modelValue,
  options = [],
  message = undefined,
  required,
  icon = undefined,
  iconVariant = 'default',
  disabled,
  loading,
  testId = undefined,
  textClass = 'font-bold',
  buttonClass = 'h-14',
  iconMarginClass = 'mr-6',
  iconClass = undefined
} = defineProps<{
  name: string;
  label: string;
  variant?: VariantType;
  placeholder?: string;
  modelValue: Value | undefined;
  options?: Array<InputSelectOptionType<Value>>;
  message?: string;
  required?: boolean;
  icon?: IconName;
  iconVariant?: IconVariantType;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
  textClass?: string;
  buttonClass?: string;
  iconMarginClass?: string;
  iconClass?: string;
}>();

const emit = defineEmits<{
  'update:model-value': [selectedOption: Value | undefined];
}>();

const inputRef: Ref<HTMLSelectElement | undefined> = ref(undefined);
const isFocused = ref(false);
const isDisabled = computed(() => disabled || loading);

// Check if some content is passed to the 'icon' slot
const slots = useSlots();
let hasIconSlot = false;
if (slots.icon && slots.icon()[0]?.children?.length) {
  hasIconSlot = true;
}

// Build options list with placeholder if needed
const optionsList = computed(() => {
  const result: InputSelectOptionType<Value>[] = placeholder
    ? [
        {
          value: '' as Value,
          label: placeholder,
          disabled: required,
          isPlaceholder: true
        }
      ]
    : [];
  if (options.length) {
    result.push(...options);
  }
  return result;
});

const selectedOption = computed({
  get() {
    return optionsList.value.filter((option: InputSelectOptionType<Value>) => option.value === modelValue)[0];
  },
  set(currentOption: InputSelectOptionType<Value> | undefined) {
    emit('update:model-value', currentOption?.value);
  }
});

const textClx = computed(() => ({
  'text-default-neutral-217': !modelValue
}));

const iconClx = computed(() => ({
  'text-default-neutral-217': isDisabled.value && iconVariant === 'default',
  'text-primary-disabled': isDisabled.value && iconVariant === 'primary',
  'text-default': !isDisabled.value && iconVariant === 'default',
  'text-primary': !isDisabled.value && iconVariant === 'primary'
}));

const valueButtonClx = computed(() => ({
  'text-default-disabled': isDisabled.value || selectedOption.value === undefined
}));

const optionsClx = computed(() => ({
  'border-primary': isFocused.value,
  'border-default-neutral-217': !isFocused.value
}));

onBeforeMount(() => {
  // Empty modelValue if his value does not exists or is disabled
  const selectedOption = optionsList.value.filter((option) => option.value === modelValue)[0];
  if (!selectedOption || selectedOption.disabled) {
    emit('update:model-value', undefined);
  }
});
</script>

<template>
  <InputBase v-bind="{ variant, label, message, required, disabled, loading, inputRef }" :not-rounded-on-focus="true">
    <template #icon>
      <slot name="icon" />
    </template>
    <template #default="scope">
      <Listbox v-model="selectedOption" as="div" class="w-full" by="value" :disabled="isDisabled" :data-testid="testId">
        <div class="relative">
          <ListboxButton
            :class="[valueButtonClx, buttonClass]"
            class="relative w-full text-left px-6 flex items-center"
          >
            <!-- The icon must be define here in order to have full option width -->
            <div
              v-if="selectedOption?.icon || icon || hasIconSlot"
              :class="[iconClx, iconMarginClass]"
              class="flex items-center shrink-0"
            >
              <component
                :is="selectedOption.icon"
                v-if="selectedOption?.icon"
                :class="iconClass"
                class="w-5 h-5 shrink-0"
              />
              <template v-else>
                <VueFeather v-if="icon" size="22" :type="icon" />
                <div v-else class="w-5 h-5">
                  <slot name="icon" />
                </div>
              </template>
            </div>
            <div :class="[textClx, textClass]" class="truncate w-full">
              {{ selectedOption?.label || placeholder }}
            </div>
            <div :class="iconClx">
              <VueFeather size="22" :type="isFocused ? 'chevron-up' : 'chevron-down'" class="ml-6 flex items-center" />
            </div>
          </ListboxButton>

          <ListboxOptions
            v-if="!disabled"
            :class="optionsClx"
            class="absolute z-10 max-h-60 w-full overflow-auto border rounded-b shadow-lg bg-white"
            :style="{
              'margin-left': '-1px',
              width: 'calc(100% + 2px)'
            }"
            @focus="(scope.focus(), (isFocused = true))"
            @blur="(scope.blur(), (isFocused = false))"
          >
            <InputSelectOption v-bind="{ options: optionsList }"></InputSelectOption>
          </ListboxOptions>
        </div>
      </Listbox>
    </template>
  </InputBase>
</template>
